import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  Plus,
  Search,
  Filter,
  Loader2,
  Archive,
  AlertTriangle,
  CheckCircle2,
  Clock,
  TrendingUp,
  FileText,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { listConsultations, archiveConsultation } from "@/lib/adapters/consultations";

const RISK_OPTIONS = [
  { value: "", label: "All risks" },
  { value: "low", label: "Low" },
  { value: "moderate", label: "Moderate" },
  { value: "high", label: "High" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "analyzing", label: "Analyzing" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
];

const CATEGORY_OPTIONS = [
  { value: "", label: "All treatments" },
  { value: "rct", label: "RCT" },
  { value: "implant", label: "Implant" },
  { value: "extraction", label: "Extraction" },
  { value: "crown_bridge", label: "Crown & Bridge" },
  { value: "orthodontics", label: "Orthodontics" },
  { value: "scaling", label: "Scaling" },
  { value: "denture", label: "Denture" },
  { value: "pediatric", label: "Pediatric" },
  { value: "oral_surgery", label: "Oral Surgery" },
  { value: "periodontal", label: "Periodontal" },
  { value: "cosmetic", label: "Cosmetic" },
  { value: "other", label: "Other" },
];

const PAGE_SIZE = 9;

function riskOf(c) {
  const explicit = (c.risk_level || c.riskLevel || c.risk || "").toLowerCase();
  if (explicit) return explicit;
  return "unknown";
}

function statusOf(c) {
  return (c.status || c.analysis_status || (c.archived_at ? "archived" : "pending")).toLowerCase();
}

function categoryOf(c) {
  const raw = c.treatment_category || c.treatmentCategory;
  if (raw) return String(raw).toLowerCase();
  const types = c.dental_problem_types || c.dentalProblemTypes;
  if (Array.isArray(types) && types[0]) {
    return String(types[0]).replace(/_(required|case)$/, "").toLowerCase();
  }
  return "";
}

function nameOf(c) {
  return c.patient_name || c.patientName || c.full_name || c.fullName || "Patient";
}

function dateOf(c) {
  return c.created_at || c.createdAt || c.updated_at || c.updatedAt || null;
}

function caseIdOf(c) {
  return c.case_id || c.caseId || c.id;
}

function isWithinThisMonth(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

export default function AiConsultantDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [q, setQ] = useState("");
  const [risk, setRisk] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    listConsultations({})
      .then((data) => {
        const rows = Array.isArray(data) ? data : (data.items || data.data || data.consultations || []);
        setItems(rows);
      })
      .catch((err) => {
        console.error("[dashboard] load failed", err);
        setError(err?.response?.status === 401 ? "Please sign in to see your saved cases." : "Could not load your cases.");
        setItems([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((c) => {
      if (term) {
        const hay = `${nameOf(c)} ${c.chief_complaint || c.chiefComplaint || ""} ${caseIdOf(c) || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (risk && riskOf(c) !== risk) return false;
      if (status && statusOf(c) !== status) return false;
      if (category && categoryOf(c) !== category) return false;
      const created = dateOf(c);
      if (dateFrom && (!created || new Date(created) < new Date(dateFrom))) return false;
      if (dateTo && (!created || new Date(created) > new Date(dateTo + "T23:59:59"))) return false;
      return true;
    });
  }, [items, q, risk, status, category, dateFrom, dateTo]);

  const sorted = useMemo(() => {
    const cp = [...filtered];
    cp.sort((a, b) => {
      const ad = new Date(dateOf(a) || 0).getTime();
      const bd = new Date(dateOf(b) || 0).getTime();
      if (sort === "newest") return bd - ad;
      if (sort === "oldest") return ad - bd;
      if (sort === "name") return nameOf(a).localeCompare(nameOf(b));
      return 0;
    });
    return cp;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const stats = useMemo(() => {
    const s = { open: 0, completed: 0, high: 0, month: 0 };
    for (const c of items) {
      const st = statusOf(c);
      if (st === "completed") s.completed += 1;
      else if (st !== "archived") s.open += 1;
      if (riskOf(c) === "high") s.high += 1;
      if (isWithinThisMonth(dateOf(c))) s.month += 1;
    }
    return s;
  }, [items]);

  const archive = async (caseId) => {
    if (!window.confirm("Archive this case?")) return;
    try {
      await archiveConsultation(caseId);
      toast.success("Case archived");
      load();
    } catch {
      toast.error("Could not archive — please try again");
    }
  };

  const clearFilters = () => {
    setQ(""); setRisk(""); setStatus(""); setCategory(""); setDateFrom(""); setDateTo(""); setSort("newest");
  };

  return (
    <main className="dyntra-container py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#F3EFFC] flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-[#4B2AAD]" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#4B2AAD]">Dr. Dyntra AI</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900">AI Consultant</h1>
            <p className="text-sm text-gray-600 mt-1">Your saved cases, all in one place.</p>
          </div>
        </div>
        <Link
          to="/ai-consultant/new"
          data-testid="dash-new-case"
          className="dyntra-btn-primary px-5 py-3 text-sm"
        >
          <Plus className="h-4 w-4" /> New consultation
        </Link>
      </div>

      <section className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Clock} tone="purple" label="Open cases" value={stats.open} />
        <StatCard icon={CheckCircle2} tone="emerald" label="Completed" value={stats.completed} />
        <StatCard icon={AlertTriangle} tone="rose" label="High risk" value={stats.high} />
        <StatCard icon={TrendingUp} tone="amber" label="This month" value={stats.month} />
      </section>

      <section className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/ai-consultant/new"
          data-testid="dash-new-card"
          className="rounded-2xl border-2 border-dashed border-[#4B2AAD]/40 bg-[#F3EFFC]/40 p-6 flex flex-col items-start hover:border-[#4B2AAD] hover:bg-[#F3EFFC]/70 transition-colors group"
        >
          <div className="h-10 w-10 rounded-xl bg-[#4B2AAD] text-white flex items-center justify-center">
            <Plus className="h-5 w-5" />
          </div>
          <h3 className="mt-3 font-display text-lg font-bold text-gray-900">Start a new consultation</h3>
          <p className="text-sm text-gray-600 mt-1">Quick intake, guided AI chat, clinical report — end-to-end in minutes.</p>
        </Link>
        <div className="md:col-span-2 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#F3EFFC] flex items-center justify-center">
              <FileText className="h-5 w-5 text-[#4B2AAD]" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-gray-900">Your saved cases</h3>
              <p className="text-sm text-gray-600 mt-1">
                Every case you analyze is saved here with its report, chat history, and workflow notes. Filter or search below to jump right in.
              </p>
              <p className="text-xs text-gray-400 mt-2">
                {items.length} total cases · {stats.completed} completed · {stats.open} in progress
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-8">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              data-testid="dash-search"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search patient name, complaint, case ID"
              className="w-full rounded-xl border border-gray-200 pl-9 pr-4 py-2.5 text-sm bg-white outline-none focus:border-[#4B2AAD]"
            />
          </div>
          <select
            data-testid="dash-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm bg-white outline-none focus:border-[#4B2AAD]"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="name">Name (A–Z)</option>
          </select>
          <button
            type="button"
            data-testid="dash-toggle-filters"
            onClick={() => setShowFilters((v) => !v)}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 rounded-2xl border border-gray-100 bg-white p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Select label="Risk" testid="dash-filter-risk" value={risk} onChange={(v) => { setRisk(v); setPage(1); }} options={RISK_OPTIONS} />
            <Select label="Treatment" testid="dash-filter-category" value={category} onChange={(v) => { setCategory(v); setPage(1); }} options={CATEGORY_OPTIONS} />
            <Select label="Status" testid="dash-filter-status" value={status} onChange={(v) => { setStatus(v); setPage(1); }} options={STATUS_OPTIONS} />
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Date range</label>
              <div className="mt-1 flex items-center gap-2">
                <input data-testid="dash-filter-from" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="w-full rounded-xl border border-gray-200 px-2 py-2 text-sm outline-none focus:border-[#4B2AAD]" />
                <span className="text-xs text-gray-400">to</span>
                <input data-testid="dash-filter-to" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="w-full rounded-xl border border-gray-200 px-2 py-2 text-sm outline-none focus:border-[#4B2AAD]" />
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
              <button type="button" onClick={clearFilters} className="text-xs font-semibold text-[#4B2AAD] hover:underline">Clear filters</button>
            </div>
          </div>
        )}
      </section>

      <section className="mt-6">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#4B2AAD]" /></div>
        ) : error ? (
          <div className="rounded-2xl border border-gray-100 bg-white p-10 text-center">
            <AlertTriangle className="h-6 w-6 text-amber-500 mx-auto" />
            <p className="mt-2 text-sm text-gray-700">{error}</p>
          </div>
        ) : paged.length === 0 ? (
          <div className="rounded-2xl border border-gray-100 bg-white p-16 text-center">
            <FileText className="h-10 w-10 text-gray-300 mx-auto" />
            <h3 className="mt-3 font-display text-lg font-bold text-gray-900">No cases yet</h3>
            <p className="text-sm text-gray-500 mt-1">
              Start your first AI consultation to see the analysis right here.
            </p>
            <Link to="/ai-consultant/new" className="dyntra-btn-primary mt-4 inline-flex px-5 py-2.5 text-sm">
              <Plus className="h-4 w-4" /> New consultation
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paged.map((c) => <SavedCard key={caseIdOf(c)} c={c} onArchive={archive} />)}
            </div>
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-2">
                <button type="button" data-testid="dash-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="h-9 w-9 rounded-lg border border-gray-200 disabled:opacity-40 flex items-center justify-center">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                <button type="button" data-testid="dash-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-9 w-9 rounded-lg border border-gray-200 disabled:opacity-40 flex items-center justify-center">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}

const TONE_STYLES = {
  purple: { bg: "bg-[#F3EFFC]", fg: "text-[#4B2AAD]" },
  emerald: { bg: "bg-emerald-50", fg: "text-emerald-700" },
  rose: { bg: "bg-rose-50", fg: "text-rose-700" },
  amber: { bg: "bg-amber-50", fg: "text-amber-700" },
};

function StatCard({ icon: Icon, tone, label, value }) {
  const style = TONE_STYLES[tone] || TONE_STYLES.purple;
  return (
    <div data-testid={`dash-stat-${label.replace(/\s/g, "-").toLowerCase()}`} className="rounded-2xl border border-gray-100 bg-white p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl ${style.bg} flex items-center justify-center`}>
        <Icon className={`h-5 w-5 ${style.fg}`} />
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</p>
        <p className="font-display text-xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
}

function SavedCard({ c, onArchive }) {
  const caseId = caseIdOf(c);
  const risk = riskOf(c);
  const st = statusOf(c);
  const cat = categoryOf(c);
  const created = dateOf(c);
  return (
    <div data-testid={`saved-case-${caseId}`} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-display text-base font-bold text-gray-900 truncate">{nameOf(c)}</h3>
          <p className="text-xs text-gray-500 truncate">#{caseId}</p>
        </div>
        <RiskBadge risk={risk} />
      </div>
      <p className="mt-3 text-sm text-gray-700 line-clamp-2 min-h-[2.5rem]">
        {c.chief_complaint || c.chiefComplaint || "No chief complaint recorded"}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        {cat && <span className="dyntra-chip">{cat.replace(/_/g, " ")}</span>}
        <StatusChip status={st} />
        {created && <span className="text-gray-500">{new Date(created).toLocaleDateString()}</span>}
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Link to={`/ai-consultant/${caseId}`} className="dyntra-btn-primary flex-1 justify-center py-2 text-sm">Open</Link>
        <Link to={`/ai-consultant/${caseId}/report`} className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Report</Link>
        <button
          type="button"
          onClick={() => onArchive(caseId)}
          data-testid={`saved-archive-${caseId}`}
          className="h-9 w-9 rounded-xl border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-500"
          aria-label="Archive case"
        >
          <Archive className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function RiskBadge({ risk }) {
  const map = {
    high: { bg: "bg-rose-100", fg: "text-rose-700", label: "High risk" },
    moderate: { bg: "bg-amber-100", fg: "text-amber-700", label: "Moderate" },
    low: { bg: "bg-emerald-100", fg: "text-emerald-700", label: "Low risk" },
    unknown: { bg: "bg-gray-100", fg: "text-gray-600", label: "Pending" },
  };
  const m = map[risk] || map.unknown;
  return <span className={`inline-flex items-center gap-1 rounded-full ${m.bg} ${m.fg} text-xs font-semibold px-2 py-0.5`}>{m.label}</span>;
}

function StatusChip({ status }) {
  const map = {
    completed: { bg: "bg-emerald-50", fg: "text-emerald-700", label: "Completed" },
    analyzing: { bg: "bg-[#F3EFFC]", fg: "text-[#4B2AAD]", label: "Analyzing" },
    pending: { bg: "bg-amber-50", fg: "text-amber-700", label: "Pending" },
    archived: { bg: "bg-gray-100", fg: "text-gray-600", label: "Archived" },
  };
  const m = map[status] || map.pending;
  return <span className={`inline-flex items-center rounded-full ${m.bg} ${m.fg} text-xs font-semibold px-2 py-0.5`}>{m.label}</span>;
}

function Select({ label, testid, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white outline-none focus:border-[#4B2AAD]"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
